/* eslint-disable camelcase */
import React, {useState, useReducer} from 'react';
import PropTypes from 'prop-types';
import Avatar from 'react-avatar';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPencilAlt} from '@fortawesome/free-solid-svg-icons';
import {TrashIcon} from '@heroicons/react/outline';
import cx from 'classnames';

import Link from './Link';
import {useAppContext, useAnalytics} from '../hooks';
import {MemberBadge, CoachBadge} from './MemberBadge';
import {UpvoteButton} from './UpvoteButton';
import {CommentMarkdown} from './Markdown';
import {getEnglishTime} from '../util';
import CommentEdit from './CommentEdit';
import {ReplyIcon, PinIcon} from './svg';
import {ReplyEditor} from './CommentEditor';
import KarmaPill from './KarmaPill';
import {PermissionType} from '../../../lib/constants/permissions';
import {postCommentVote, deleteCommentVote, updateComment} from '../api/api';

const minLength = 250;

const votingReducer = (voteState, voteAction) => {
  switch (voteAction.type) {
    case 'add':
      postCommentVote(voteAction.id);
      return {voted: true, count: voteState.count + 1};
    case 'remove':
      deleteCommentVote(voteAction.id);
      return {voted: false, count: voteState.count - 1};
    default:
      return voteState;
  }
};

const Comment = (props) => {
  const {
    questionID,
    replies,
    isReply,
    isGated,
    onTriggerSignup,
    onSubmit,
    submitted = false,
  } = props;

  const {
    id,
    voters,
    displayName,
    karma,
    uuid,
    coach_slug,
    data,
    profile_img,
    created_at,
    updated_at,
    pinned,
    is_member: isMember,
  } = props.data;

  const {tracker} = useAnalytics();
  const {currentUser, hasPermission} = useAppContext();
  const [readMore, setReadMore] = useState(data.text.length < minLength);
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [text, setText] = useState(data.text);
  const [vote, dispatchVote] = useReducer(votingReducer, {
    count: voters.length,
    voted: currentUser && voters.includes(currentUser.id),
  });

  const isTopAnswer = pinned && !isReply;
  const isAuthor = currentUser && currentUser.uuid === uuid;
  const isCoach = coach_slug;
  const isEdited = created_at !== updated_at;
  const timestamp = getEnglishTime(isEdited ? updated_at : created_at);

  // Permissions
  const canEdit = isAuthor || hasPermission(PermissionType.QUESTION_EDIT);
  const canPin = !isReply && hasPermission(PermissionType.QUESTION_PIN);
  const canArchive = isAuthor || hasPermission(PermissionType.QUESTION_ARCHIVE);
  const canReply = !isReply && !isGated && currentUser && currentUser.id;

  const onEdit = (questionData) => {
    setText(questionData);
    setIsEditing(false);
  };

  const onClickVote = () => {
    if (isGated) {
      onTriggerSignup();
    } else {
      tracker.questionUpvoteComment();
      dispatchVote({type: !vote.voted ? 'add' : 'remove', id});
    }
  };

  const onClickUser = (e) => {
    if (isGated) {
      e.preventDefault();
      onTriggerSignup();
    }
  };

  const onPin = async (isPinned) => {
    await updateComment(id, {pinned: isPinned});
    window.location.reload();
  };

  const onArchive = async () => {
    await updateComment(id, {archived: true});
    window.location.reload();
  };

  return (
    <div
      id={id}
      className={cx('comment', {
        'border border-gray-200 rounded-lg mb-3': !isReply,
        'comment--editing': isEditing,
      })}
    >
      <div className="pt-5 px-5">
        {isTopAnswer && <TopAnswerBadge />}
        <div className="flex items-center">
          <Link href={`/users/${uuid}`} onClick={onClickUser}>
            <Avatar
              className="comment-profile-img"
              name={displayName}
              src={profile_img || null}
              color="#f0f0f0"
              fgColor="#000000"
              size={40}
            />
          </Link>
          <div>
            <div className="flex">
              <Link href={`/users/${uuid}`} onClick={onClickUser} className="comment-profile-name">
                {displayName}
              </Link>
              {isCoach && (
                <CoachBadge
                  className="ml-1.5"
                  coachSlug={coach_slug}
                  onClick={() => tracker.questionClickCoachBadge({coach_slug})}
                />
              )}
              {!isCoach && isMember && (
                <MemberBadge
                  className="ml-1.5"
                  onClick={() => tracker.questionClickMemberBadge()}
                />
              )}
              {!isAuthor && (
                <KarmaPill
                  score={karma}
                  outline={isReply}
                  className="ml-1.5"
                  onClick={() => tracker.karmaClickScore({source: 'comment'})}
                />
              )}
            </div>
            <span className="text-xs font-medium text-gray-400">
              <time
                dateTime={isEdited ? updated_at : created_at}
                title={isEdited ? updated_at : created_at}
              >
                {`${isEdited ? 'Updated ' : ''}${timestamp}`}
              </time>
            </span>
          </div>
        </div>
      </div>
      {isEditing ? (
        <CommentEdit
          id={id}
          currText={text}
          onFinish={onEdit}
          isReply={isReply}
        />
      ) : (
        <div className="prose relative ml-1 px-5 pt-2">
          <div className={cx({'comment-message-chop': !readMore && !isReply})}>
            <CommentMarkdown>{text}</CommentMarkdown>
          </div>
        </div>
      )}
      {!isEditing && (
        <div className="flex items-center px-5 mt-2 pb-5 flex-wrap-reverse">
          <div className="flex items-center flex-nowrap my-1">
            {!isReply && (
              <UpvoteButton
                voted={vote.voted}
                count={vote.count}
                onClick={onClickVote}
              />
            )}
            {canReply && (
              <button
                className="CommentButton comment-reply"
                onClick={() => setIsReplying(!isReplying)}
                type="button"
              >
                <span className="CommentButton__icon-left">
                  <ReplyIcon />
                </span>
                Comment
              </button>
            )}
            {canEdit && (
              <button
                className="CommentButton"
                onClick={() => setIsEditing(true)}
                type="button"
              >
                <FontAwesomeIcon className="CommentButton__icon-left" icon={faPencilAlt} />
                Edit
              </button>
            )}
            {canPin && (
              <button
                className="CommentButton"
                onClick={() => onPin(!pinned)}
                type="button"
              >
                <FontAwesomeIcon className="CommentButton__icon-left" icon={faPencilAlt} />
                {pinned ? 'Unpin' : 'Pin'}
              </button>
            )}
            {canArchive && (
              <button
                className="CommentButton"
                onClick={onArchive}
                type="button"
              >
                <TrashIcon className="w-4 -ml-1 mr-1" />
                Delete
              </button>
            )}
          </div>
          {!readMore && !isReply && (
            <button
              type="button"
              className="CommentButton CommentButton--outline ml-auto my-1"
              onClick={!isGated
                ? () => setReadMore(true)
                : onTriggerSignup}
            >
              Read more
            </button>
          )}
        </div>
      )}
      <CommentReplies
        onTriggerSignup={onTriggerSignup}
        replies={replies}
        isReplying={isReplying}
        onClickReply={() => setIsReplying(true)}
        questionID={questionID}
        parentCommentID={id}
        isGated={isGated}
        onSubmit={onSubmit}
        submitted={submitted}
      />
    </div>
  );
};

const TopAnswerBadge = () => (
  <div className="TopAnswerBadge">
    <PinIcon />
    &nbsp;
    <span>top answer</span>
  </div>
);

const ReplyPreview = ({replies, onClick}) => {
  const {displayName, profile_img} = replies[0];

  const {text} = replies[0].data;

  const remaining = replies.length - 1;

  return (
    <button
      className="ReplyPreview rounded-b-lg"
      onClick={onClick}
      type="button"
    >
      <div className="ReplyPreview__reply">
        <Avatar
          className="comment-profile-img"
          name={displayName}
          src={profile_img || null}
          color="#f0f0f0"
          fgColor="#000000"
          size={24}
        />
        <span className="ReplyPreview__user">
          {displayName}
        </span>
        <span className="ReplyPreview__reply-text">
          {text}
        </span>
      </div>
      {replies.length > 1 && (
        <span className="ReplyPreview__more">
          <ReplyIcon />
          {`${remaining} more ${remaining > 1 ? 'replies' : 'reply'}`}
        </span>
      )}
    </button>
  );
};

function CommentReplies({
  onTriggerSignup,
  replies,
  isReplying,
  questionID,
  parentCommentID,
  onClickReply,
  isGated,
  onSubmit,
  submitted,
}) {
  if (!isReplying && (!replies || !replies.length)) {
    return null;
  }

  if (replies && replies.length && !isReplying) {
    return <ReplyPreview replies={replies} onClick={onClickReply} />;
  }

  return (
    <>
      <div className={cx('bg-gray-50 border-t border-gray-200', {'rounded-b-lg': isGated})}>
        {replies && replies.map((c) => (
          <Comment onTriggerSignup={onTriggerSignup} data={c} replies={null} isReply />
        ))}
      </div>
      {!isGated && (
        <ReplyEditor
          questionID={questionID}
          parentCommentID={parentCommentID}
          onSubmit={onSubmit}
          submitted={submitted}
        />
      )}
    </>
  );
}

const CommentShape = PropTypes.shape({
  id: PropTypes.number,
  voters: PropTypes.array,
  user_id: PropTypes.number.isRequired,
  displayName: PropTypes.string.isRequired,
  uuid: PropTypes.string.isRequired,
  data: PropTypes.object,
  profile_img: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  updated_at: PropTypes.string.isRequired,
  pinned: PropTypes.bool.isRequired,
  is_member: PropTypes.bool.isRequired,
});

CommentReplies.propTypes = {
  onTriggerSignup: PropTypes.string.isRequired,
  replies: PropTypes.arrayOf(CommentShape).isRequired,
  isReplying: PropTypes.bool.isRequired,
  questionID: PropTypes.string.isRequired,
  parentCommentID: PropTypes.number.isRequired,
  onClickReply: PropTypes.func.isRequired,
  isGated: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func,
  submitted: PropTypes.bool,
};

Comment.propTypes = {
  data: CommentShape.isRequired,
  questionID: PropTypes.string.isRequired,
  replies: PropTypes.arrayOf(CommentShape).isRequired,
  isReply: PropTypes.bool.isRequired,
  isGated: PropTypes.bool.isRequired,
  onTriggerSignup: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  submitted: PropTypes.bool,
};

ReplyPreview.propTypes = {
  replies: PropTypes.arrayOf(CommentShape).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default Comment;
